import {buildParticleField,VOICE_ENERGY,VOICE_PACE} from "./particleField.js";
const VERTEX=`
attribute vec3 a_position;
attribute float a_seed;
attribute float a_kind;
uniform float u_time;
uniform mediump float u_energy;
uniform float u_size;
uniform float u_dpr;
varying mediump float v_alpha;
varying mediump float v_kind;
varying mediump float v_light;
void main(){
 float t=u_time*.7;
 float len=max(length(a_position),.001);vec3 n=a_position/len;
 float f=sin(n.x*3.8+n.z*2.5+t*.67);
 float g=cos(n.y*4.7-n.x*2.4-t*.58);
 float deformation=1.+(.055+u_energy*.13)*f*g+(.045+u_energy*.035)*sin(n.z*5.2+n.y*2.8+t*.8);
 float twist=(.14+u_energy*.08)*sin(n.y*3.+t*.25);
 vec3 p=a_position*deformation;
 p.xz=mat2(cos(twist),-sin(twist),sin(twist),cos(twist))*p.xz;
 float rot=t*.2;
 p.xz=mat2(cos(rot),-sin(rot),sin(rot),cos(rot))*p.xz;
 p.yz=mat2(cos(.22),-sin(.22),sin(.22),cos(.22))*p.yz;
 float perspective=3.6/(3.6-p.z*.45);
 gl_Position=vec4(p.xy*.6*perspective,0.,1.);
 float coverage=clamp((sin(n.x*3.3+t*.2)*cos(n.z*4.1-t*.16)+.45*sin(n.y*5.7+t*.19)+.15)/.9,0.,1.);
 float depth=clamp((p.z+1.4)/2.8,0.,1.);
 v_alpha=(.1+.9*coverage)*(.28+.72*depth);
 if(a_kind>.5)v_alpha=.16+.42*depth;
 if(a_kind>1.5)v_alpha=.32+u_energy*.28;
 v_kind=a_kind;v_light=depth;
 gl_PointSize=(1.3+1.5*depth+.5*sin(a_seed*3.))*u_dpr;
 if(a_kind>.5)gl_PointSize=(.8+1.5*depth)*u_dpr;
 if(a_kind>1.5)gl_PointSize=min(96.,u_size*(.14+u_energy*.035));
}
`;
const FRAGMENT=`
precision mediump float;
uniform float u_points;
uniform mediump float u_energy;
varying mediump float v_alpha;
varying mediump float v_kind;
varying mediump float v_light;
void main(){
 float alpha=v_alpha;
 vec3 color=mix(vec3(.13,.52,.9),vec3(.55,.93,1.),v_light);
 if(u_points>.5){
  float d=length(gl_PointCoord-.5)*2.;
  if(d>1.)discard;
  alpha*=pow(max(0.,1.-d),1.6);
  if(v_kind>1.5){alpha*=exp(-d*d*6.)*(.65+u_energy*.25);color=vec3(.18,.7,1.);}
 }else{
  alpha=pow(alpha,1.15)*(.77+u_energy*.5);
  color=mix(vec3(.08,.43,.8),vec3(.37,.85,1.),v_light);
 }
 gl_FragColor=vec4(color*alpha,alpha);
}
`;
export function createPlasmaRenderer(canvas,{size,small}){
 let gl,program,vertex,fragment;const buffers=[];
 const dispose=()=>{if(!gl||typeof gl.deleteProgram!=="function")return;for(const b of buffers)gl.deleteBuffer(b);if(program)gl.deleteProgram(program);if(vertex)gl.deleteShader(vertex);if(fragment)gl.deleteShader(fragment);};
 try{
  gl=canvas.getContext("webgl",{alpha:true,antialias:false,depth:false,stencil:false,premultipliedAlpha:true,powerPreference:"low-power"});
  if(!gl||typeof gl.createShader!=="function")return null;
  const compile=(type,source)=>{const shader=gl.createShader(type);gl.shaderSource(shader,source);gl.compileShader(shader);if(!gl.getShaderParameter(shader,gl.COMPILE_STATUS)){gl.deleteShader(shader);throw Error("Particle shader unavailable")}return shader;};
  vertex=compile(gl.VERTEX_SHADER,VERTEX);fragment=compile(gl.FRAGMENT_SHADER,FRAGMENT);
  program=gl.createProgram();gl.attachShader(program,vertex);gl.attachShader(program,fragment);gl.linkProgram(program);
  if(!gl.getProgramParameter(program,gl.LINK_STATUS))throw Error("Particle program unavailable");
  gl.useProgram(program);
  const field=buildParticleField(small),upload=data=>{const b=gl.createBuffer();buffers.push(b);gl.bindBuffer(gl.ARRAY_BUFFER,b);gl.bufferData(gl.ARRAY_BUFFER,data,gl.STATIC_DRAW);return b;};
  const pointBuffer=upload(field.points),lineBuffer=upload(field.lines);
  const position=gl.getAttribLocation(program,"a_position"),seed=gl.getAttribLocation(program,"a_seed"),kind=gl.getAttribLocation(program,"a_kind");
  for(const a of [position,seed,kind])gl.enableVertexAttribArray(a);
  const bind=b=>{gl.bindBuffer(gl.ARRAY_BUFFER,b);gl.vertexAttribPointer(position,3,gl.FLOAT,false,20,0);gl.vertexAttribPointer(seed,1,gl.FLOAT,false,20,12);gl.vertexAttribPointer(kind,1,gl.FLOAT,false,20,16);};
  const uniform=n=>gl.getUniformLocation(program,n);
  const time=uniform("u_time"),energyUniform=uniform("u_energy"),points=uniform("u_points");
  const dpr=Math.min(window.devicePixelRatio||1,small?1.5:1.8);
  canvas.width=Math.round(size*dpr);canvas.height=Math.round(size*dpr);gl.viewport(0,0,canvas.width,canvas.height);
  gl.uniform1f(uniform("u_size"),canvas.width);gl.uniform1f(uniform("u_dpr"),dpr);
  gl.enable(gl.BLEND);gl.blendFunc(gl.ONE,gl.ONE);gl.clearColor(0,0,0,0);
  let energy=.18,pace=.55,phase=0;
  return {render(seconds,state,delta,audioLevel){
   const blend=Math.min(1,delta*.004);const target=state==="escuchando"&&Number.isFinite(audioLevel)?Math.max(.12,Math.min(1.2,.12+audioLevel)):VOICE_ENERGY[state]??.18;energy+=(target-energy)*blend;pace+=((VOICE_PACE[state]??.55)-pace)*blend;
   if(seconds!==0)phase+=delta*.001*pace;
   gl.clear(gl.COLOR_BUFFER_BIT);gl.uniform1f(time,phase);gl.uniform1f(energyUniform,energy);
   gl.uniform1f(points,0);bind(lineBuffer);gl.drawArrays(gl.LINES,0,field.lines.length/5);
   gl.uniform1f(points,1);bind(pointBuffer);gl.drawArrays(gl.POINTS,0,field.points.length/5);
  },dispose};
 }catch{dispose();return null;}
}
